import React, { useEffect, useRef, useState } from "react";

import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Ripple } from "primereact/ripple";
import { Image } from "primereact/image";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";

import { baseURL } from "../Api/ApiConstant";
import { ProjectContact } from "../Controllers/Types/Project/ProjectContact";
import { useAppDispatch, useAppSelector } from "../Controllers/hooks";
import { fetchAllContact } from "../Controllers/Thunks/projectThunks";
import { mapShareContactType, ShareContactType } from "../Controllers/Types/Share/ShareContactType";
import { confirmDialog } from "primereact/confirmdialog";

interface ShareModalProps {
    isOpen: boolean;
    canEditOption: boolean;
    title: string;
    loading: boolean;
    sharedList: ShareContactType[];
    projectContactIds: number[];
    onShare: (selected: ShareContactType[]) => void | Promise<void>;
    onClose: () => void;
}

const ShareModal: React.FC<ShareModalProps> = ({
    isOpen,
    title,
    loading,
    sharedList,
    projectContactIds,
    onClose,
    onShare,
    canEditOption
}) => {
    const dispatch = useAppDispatch();
    const contactListBodyRef = useRef<HTMLDivElement>(null);
    const [contactAccess, setContactAccess] = useState<Record<number, { view: boolean; edit: boolean }>>({});
    const [activeContactTab, setActiveContactTab] = useState<"Employee" | "Professional" | "Client">("Employee");
    const [contactSearchQuery, setContactSearchQuery] = useState<string>('');
    const [contactsData, setContactsData] = useState<ProjectContact[]>([]);
    const [saveChanges, setSaveChanges] = useState<boolean>(false);
    const [filteredContactsData, setFilteredContactsData] = useState<ShareContactType[]>([]);

    // as main list
    const [mainPreparedList, setMainPreparedList] = useState<ShareContactType[]>([]);

    useEffect(() => {
        dispatch(fetchAllContact());
    }, [dispatch]);


    const getActiveSharedUsers = (list: ShareContactType[]) => list.filter(item => item.can_edit === 1 || item.can_view === 1);

    // Check button disabled
    const hasShareChanges = (sharedList: ShareContactType[], mainList: ShareContactType[]): boolean => {
        const activeMain = getActiveSharedUsers(mainList);

        // count mismatch → change
        if (sharedList.length !== activeMain.length) return true;

        return activeMain.some(mainUser => {
            const sharedUser = sharedList.find(
                u => u.id === mainUser.id
            );

            // new share
            if (!sharedUser) return true;

            // permission changed
            return (
                sharedUser.can_edit !== mainUser.can_edit ||
                sharedUser.can_view !== mainUser.can_view
            );
        });
    };

    useEffect(() => {
        if (!sharedList || !mainPreparedList) {
            setSaveChanges(false);
            return;
        }

        const changed = hasShareChanges(
            sharedList,
            mainPreparedList
        );

        setSaveChanges(changed);
    }, [mainPreparedList, sharedList]);

    const getShareChangeMessageForContact = (
        contact: ShareContactType
    ): string => {
        const old = sharedList.find(u => u.id === contact.id);
        const current = mainPreparedList.find(u => u.id === contact.id);

        const isActive = (u?: ShareContactType) =>
            !!u && (u.can_edit === 1 || u.can_view === 1);

        // 🆕 Newly shared
        if (!old && isActive(current)) {
            if (current!.can_edit === 1 && current!.can_view === 1) {
                return "Newly shared with edit & view access";
            }
            if (current!.can_edit === 1) {
                return "Newly shared with edit access";
            }
            return "Newly shared with view access";
        }

        // ❌ Removed share
        if (old && !isActive(current)) {
            return "Removed from shared access";
        }

        // ✏️ View → Edit
        if (
            old &&
            current &&
            old.can_view === 1 &&
            old.can_edit === 0 &&
            current.can_edit === 1
        ) {
            return "Edit access added (previously view only)";
        }

        // 👀 Edit → View
        if (
            old &&
            current &&
            old.can_edit === 1 &&
            current.can_edit === 0 &&
            current.can_view === 1
        ) {
            return "Changed to view-only access";
        }

        // 🔵 Already shared (NO CHANGE)
        if (old && current && isActive(current)) {
            if (current.can_edit === 1 && current.can_view === 1) {
                return "Already shared with edit & view access";
            }
            if (current.can_edit === 1) {
                return "Already shared with edit access";
            }
            if (current.can_view === 1) {
                return "Already shared with view access";
            }
        }

        return "";
    };

    const allcontacts = useAppSelector((state) => state.project.projectAllContact);

    // initally load contacts.
    useEffect(() => {
        if (!allcontacts || allcontacts.length === 0) return;

        if (projectContactIds && projectContactIds.length > 0) {
            const onlyProjectContacts = allcontacts.filter(contact => projectContactIds.includes(contact.id) ? true : false);
            setContactsData(onlyProjectContacts);
        } else {
            /* new changes */
            // setContactsData(allcontacts);
            setContactsData([]);
        }

    }, [allcontacts, projectContactIds]);


    // now replace with alredy check contacs

    // useEffect(() => {
    //     const checkdList: ShareContactType[] = contactsData.map((e) => {
    //         const checkLit = sharedList.find((ex) => e.id === ex.id)
    //         if (checkLit && checkLit !== null) {
    //             return checkLit;
    //         } else {
    //             const dt: any = {
    //                 id: e.id,
    //                 first_name: e.first_name,
    //                 last_name: e.last_name,
    //                 profile_image: e.profile_image,
    //                 email: e.email,
    //                 type: e.contactType,
    //                 by: e.contactSubType,
    //                 can_edit: 0,
    //                 can_view: 0,
    //             };

    //             return mapShareContactType(dt);
    //         }

    //     });

    //     setMainPreparedList(checkdList);


    // }, [contactsData, sharedList])

    useEffect(() => {
        if (!contactsData || contactsData.length === 0) {
            setMainPreparedList([]);
            return;
        }

        const preparedList: ShareContactType[] = contactsData.map((e) => {
            const matched = sharedList?.find((ex) => e.id === ex.id);

            
            if (matched) {
                return matched;
            }
            
            return mapShareContactType({
                id: e.id,
                first_name: e.first_name,
                last_name: e.last_name,
                profile_image: e.profile_image,
                email: e.email,
                type: e.contactType,
                by: e.contactSubType,
                can_edit: 0,
                can_view: 0,
            });
        });

        setMainPreparedList(preparedList);
    }, [contactsData, sharedList]);



    // initial conten set and search function
    useEffect(() => {
        if (mainPreparedList && mainPreparedList.length > 0) {
            let filteredData: ShareContactType[] = mainPreparedList;
            if (activeContactTab === 'Employee') {
                filteredData = filteredData.filter(contact => contact?.type === 'Employee');
            } else if (activeContactTab === 'Professional') {
                filteredData = filteredData.filter(contact => contact?.type === 'Professional');
            } else if (activeContactTab === 'Client') {
                filteredData = filteredData.filter(contact =>
                    contact?.type === 'Client'
                );
            }
            if (contactSearchQuery.trim() !== '') {
                const searchLower = contactSearchQuery.toLowerCase();
                filteredData = filteredData.filter(contact =>
                    contact?.first_name?.toLowerCase().includes(searchLower) ||
                    contact?.last_name?.toLowerCase().includes(searchLower)
                );
            }
            setFilteredContactsData(filteredData);
        }
    }, [mainPreparedList, activeContactTab, contactSearchQuery]);


    const handleCloseShareModal = () => {
        onClose();
        setActiveContactTab('Employee');
    }

    const shareContactModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {title ? title : 'Share'}
            </h5>

            <button
                type="button"
                className="close_modal_btn p-ripple"
                onClick={handleCloseShareModal}
            >
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const shareContactModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseShareModal}
            />
            <Button
                disabled={!saveChanges}
                label={`${loading ? "Processing" : "Share"}`}
                loading={loading}
                onClick={() => onShare(mainPreparedList)}
                className="custom_btn primary"
            />
        </div>
    );

    const handleSelectContactType = (type: "Employee" | "Professional" | "Client") => {
        setActiveContactTab(type);
    }

    const updateContactAccess = (contact: ShareContactType, updates: Partial<ShareContactType>) => {
        const updatedContact: ShareContactType = mapShareContactType({
            ...contact,
            ...updates,
        });

        setMainPreparedList(prev => {
            // Replace or add updated contact
            const updated = prev.filter(c => c.id !== contact.id).concat(updatedContact);

            // Preserve original order
            return mainPreparedList.map(fc => updated.find(u => u.id === fc.id) || fc);
        });
    };

    const handleCheckboxChangeForView = (contact: ShareContactType, checked: boolean) => {
        if (checked === true) {
            updateContactAccess(contact, {
                can_view: 1,
                can_edit: contact.can_edit,
            });
        } else {
            confirmDialog({
                message: 'Are you sure you want to unshare view access?',
                header: 'Unshare Confirmation',
                icon: 'bi bi-info-circle',
                defaultFocus: 'accept',
                acceptClassName: 'p-button-danger',
                accept: () => {
                    updateContactAccess(contact, {
                        can_view: 0,
                        can_edit: 0,
                    });
                },
            });
        }
    };

    const handleCheckboxChangeForEdit = (contact: ShareContactType, checked: boolean) => {
        if (checked === true) {
            updateContactAccess(contact, {
                can_edit: 1,
                can_view: 1,
            });
        } else {
            confirmDialog({
                message: 'Are you sure you want to unshare edit access?',
                header: 'Unshare Confirmation',
                icon: 'bi bi-info-circle',
                defaultFocus: 'accept',
                acceptClassName: 'p-button-danger',
                accept: () => {
                    updateContactAccess(contact, {
                        can_edit: 0,
                        can_view: contact.can_view,
                    });
                },
            });
        }
    };

    const getContactCount = (type: 'Employee' | 'Professional' | 'Client'): number => {
        return contactsData?.filter(data => data?.contactType === type)?.length
    }

    return (
        <Dialog
            visible={isOpen}
            header={shareContactModalHeader}
            footer={shareContactModalFooter}
            headerClassName="custom_modal_header"
            className="custom_modal_dialog modal_dialog_sm"
            onHide={handleCloseShareModal}
            contentClassName="py-0"
            dismissableMask
        >
            <div className="custom_modal_body fixed_height" ref={contactListBodyRef}>
                <div className="pt-0 ps-2 pe-2 pb-2 w-100">
                    <div className="share_contact_tab_header">
                        <div className="contact_tabs">
                            <div className="contact_tabs">
                                <div className="contact_tab_item">
                                    <button
                                        type="button"
                                        role="tab"
                                        className={`contact_tab_btn ${activeContactTab === "Employee" ? "active" : ""} p-ripple`}
                                        onClick={() => handleSelectContactType("Employee")}
                                    >
                                        Employee&nbsp;
                                        <small>
                                            ({String(getContactCount('Employee')).padStart(2, '0')})
                                        </small>
                                        <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />
                                    </button>
                                </div>

                                <div className="contact_tab_item">
                                    <button
                                        type="button"
                                        role="tab"
                                        className={`contact_tab_btn ${activeContactTab === "Professional" ? "active" : ""} p-ripple`}
                                        onClick={() => handleSelectContactType("Professional")}
                                    >
                                        Professional&nbsp;
                                        <small>
                                            ({String(getContactCount('Professional')).padStart(2, '0')})
                                        </small>
                                        <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />
                                    </button>
                                </div>

                                <div className="contact_tab_item">
                                    <button
                                        type="button"
                                        role="tab"
                                        className={`contact_tab_btn ${activeContactTab === "Client" ? "active" : ""} p-ripple`}
                                        onClick={() => handleSelectContactType("Client")}
                                    >
                                        Client&nbsp;
                                        <small>
                                            ({String(getContactCount('Client')).padStart(2, '0')})
                                        </small>
                                        <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="filter_input_grp w-100" style={{ margin: '10px 0 0' }}>
                            <InputText
                                key={`contactSearchQuery`}
                                id="contactSearchQuery"
                                value={contactSearchQuery}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setContactSearchQuery(e.target.value)}
                                className="custom_form_input"
                                placeholder="Search contact here"
                                type={"search"}
                            />
                            {contactSearchQuery ? (
                                <i
                                    className="bi bi-x-lg data_search_input_clear"
                                    onClick={() => setContactSearchQuery("")}
                                ></i>
                            ) : (
                                <i className="bi bi-search"></i>
                            )}
                        </div>
                    </div>

                    <div className="row">
                        {filteredContactsData && filteredContactsData?.length > 0 ? (
                            <div className="col-12 custom_padding_x">
                                <div className="access_head pt-2">
                                    <div className="access">
                                        <h6>View</h6>
                                    </div>
                                    {(canEditOption) && (<div className="access">
                                        <h6>Edit</h6>
                                    </div>)}
                                </div>

                                <ul className="assign_contact_list_area mb-2">
                                    {filteredContactsData?.map((contact, index) => {
                                        const isSelected = contact?.can_view === 1;

                                        return (
                                            <li
                                                key={index}
                                                className="assign_contact_list share_contact_list w-100"
                                            >
                                                <button
                                                    className={`contact_slide_tab assign_contact pointer-event-none p-ripple ${isSelected ? "active" : ""}`}
                                                // onClick={() => handleSelectContact(contact)}
                                                >
                                                    {contact?.profile_image ? (
                                                        <Image
                                                            className="contact_profile_img"
                                                            src={` ${baseURL}/${contact?.profile_image}`}
                                                            alt={contact?.first_name + " " + contact?.last_name}
                                                        />
                                                    ) : (
                                                        <div className="contact_no_profile">
                                                            {(contact?.first_name?.[0] || 'U') + (contact?.last_name?.[0] || 'U')}
                                                        </div>
                                                    )}
                                                    <div className="contact_slide_det">
                                                        <h6>
                                                            {contact?.first_name + " " + contact?.last_name}
                                                        </h6>
                                                        <p className="text-lowercase">
                                                            {contact?.email ? contact?.email : "---"}
                                                        </p>
                                                        <span className="share_info">{getShareChangeMessageForContact(contact)}</span>
                                                    </div>
                                                    {/* <Ripple pt={{ root: { style: { background: "rgba(139, 96, 253, 0.2)" } } }} /> */}


                                                </button>

                                                <div className="access_check_area">
                                                    <Checkbox
                                                        onChange={(e) => handleCheckboxChangeForView(contact, e.checked || false)}
                                                        checked={contact?.can_view === 1 ? true : false}
                                                    />
                                                </div>

                                                {(canEditOption) && (<div className="access_check_area">
                                                    <Checkbox
                                                        onChange={(e) => handleCheckboxChangeForEdit(contact, e.checked || false)}
                                                        checked={contact?.can_edit === 1 ? true : false}
                                                    />
                                                </div>)}
                                            </li>
                                        )
                                    })}
                                </ul>
                            </div>
                        ) : (
                            <div className="col-12 pt-4 h-100">
                                <div className="no_data_found d-flex align-content-center"
                                    style={{ height: ((contactListBodyRef?.current?.offsetHeight || 0) - 200) || 'auto' }}>
                                    <h6 className="m-auto">No contacts found!</h6>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Dialog>
    )
}

export default ShareModal;